import React, { useState, useEffect, useCallback, useMemo } from 'react';
import APIService from '../services/api';

/**
 * Entretiens du besoin — convoquer chez le client, à la date qu'il a fixée.
 *
 * Le lot est gagné, le client dit « on le voit mardi 14h dans nos locaux ».
 * C'est l'INVERSE de l'invitation à créneaux envoyée au candidat : ici la date
 * est imposée, il n'y a rien à choisir — on la recopie et on convoque.
 *
 * L'écran est donc un formulaire court et une liste, pas un parcours. Un
 * champ manquant se voit avant l'envoi : l'adresse est ce que le consultant
 * vient chercher, et c'est le seul champ dont l'absence fait rappeler le
 * commercial le matin même.
 *
 * Le modèle d'email vient de la BIBLIOTHÈQUE CENTRALE de l'organisation
 * (`/email-templates`) — la même que les règles de notification. On n'écrit
 * pas un éditeur de plus ici : on choisit dans l'étagère commune, et le
 * serveur dit à l'avance ce qu'un modèle ne saura pas remplir dans une
 * convocation.
 *
 * Routes : GET /besoins/:id/entretiens ·
 *          POST /besoins/:id/candidats/:id/convocation ·
 *          GET /email-templates?contexte=convocation
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const nomDe = (c) => `${c?.prenom || ''} ${c?.nom || ''}`.trim() || c?.email || 'Candidat';
const quand = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};
// Un entretien passé n'est plus une convocation à surveiller : il est fait.
const ETAT = {
  confirme: ['Convoqué', 'ok'],
  propose: ['En attente de réponse', 'warn'],
  annule: ['Annulé', 'bad'],
};

const VIDE = { candidate_id: '', debut: '', duree_min: '60', lieu: '', contact_nom: '', contact_tel: '', contact_email: '', consigne: '', template_id: '' };

export default function BesoinEntretiens({ besoin, candidats = [], showNotification }) {
  const [liste, setListe] = useState(null);     // null = chargement | 'absent'
  const [form, setForm] = useState(null);       // VIDE quand ouvert
  const [busy, setBusy] = useState(false);
  const [champErreur, setChampErreur] = useState(null);  // { champ, message }
  const [modeles, setModeles] = useState([]);   // bibliothèque d'emails de l'org

  // La bibliothèque est CENTRALE : les mêmes modèles servent aux règles de
  // notification. On les charge annotés pour ce contexte — le serveur dit
  // lesquels utilisent une variable qui n'existe pas dans une convocation.
  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/email-templates?contexte=convocation`, { headers: authH() });
        if (!r.ok) return;               // route absente : on reste sur l'email intégré
        const j = await r.json().catch(() => ({}));
        if (vivant && Array.isArray(j.data)) setModeles(j.data);
      } catch { /* réseau : l'email intégré part quand même */ }
    })();
    return () => { vivant = false; };
  }, []);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/entretiens`, { headers: authH() });
      if ([404, 405, 501].includes(r.status)) { setListe('absent'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setListe(Array.isArray(j.data) ? j.data : []);
    } catch { setListe('absent'); }
  }, [besoin.id]);
  useEffect(() => { charger(); }, [charger]);

  // Les candidats positionnés sur CE besoin : on ne convoque pas quelqu'un
  // qui n'a pas été proposé au client.
  const convocables = useMemo(() => candidats
    .map(c => ({ id: c.candidate_id ?? c.candidat_id ?? c.id, nom: nomDe(c), email: c.email || null }))
    .filter(c => c.id != null), [candidats]);

  const envoyer = async () => {
    setChampErreur(null);
    if (!form.candidate_id) { setChampErreur({ champ: 'candidate_id', message: 'Choisissez qui est convoqué.' }); return; }
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/candidats/${form.candidate_id}/convocation`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({
          debut: form.debut, duree_min: Number(form.duree_min) || 60, lieu: form.lieu,
          contact_nom: form.contact_nom || null, contact_tel: form.contact_tel || null,
          contact_email: form.contact_email || null, consigne: form.consigne || null,
          template_id: form.template_id ? Number(form.template_id) : null,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && !j.error) {
        showNotification?.('Les convocations arrivent côté serveur — rien n\'est parti.', 'info'); return;
      }
      if (!r.ok || j.success === false) {
        // Le serveur DÉSIGNE le champ fautif (lieu, debut, email) : on
        // l'accroche là où on corrige, pas dans une notification qui s'évapore.
        if (j.champ) { setChampErreur({ champ: String(j.champ), message: j.error || `HTTP ${r.status}` }); return; }
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      // L'email qui n'est pas parti ne s'annonce pas comme envoyé : sinon on
      // attend quelqu'un qui n'a jamais été prévenu.
      if (j.email_envoye === false) showNotification?.(j.message || j.email_erreur || 'Convocation enregistrée, mais l\'email n\'est pas parti.', 'error');
      else showNotification?.(j.message || 'Convocation envoyée.', 'success');
      setForm(null);
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const modeleChoisi = useMemo(
    () => modeles.find(m => String(m.id) === String(form?.template_id)) || null,
    [modeles, form]);

  const maj = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (champErreur?.champ === k) setChampErreur(null); };
  const sur = (k) => champErreur?.champ === k;
  const bord = (k) => (sur(k) ? { borderColor: 'var(--danger-bd)' } : undefined);

  return (
    <div className="bd-sect" style={{ padding: '18px' }}>
      <div className="flex items-center justify-between" style={{ marginBottom: '14px' }}>
        <h2 className="bd-sect-title"><BI icon="calendar-check" /> Entretiens chez le client</h2>
        {!form && liste !== 'absent' && (
          <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => { setForm({ ...VIDE }); setChampErreur(null); }}>
            <BI icon="plus-lg" /> Convoquer un candidat
          </button>
        )}
      </div>

      {liste === 'absent' && (
        <div className="bd-sect-hint">Les convocations arrivent côté serveur — l'écran est prêt, rien n'est enregistré d'ici là.</div>
      )}

      {form && (
        <div className="besoin-conv-form">
          <p className="besoin-conv-aide">
            Le client a fixé la date : recopiez-la telle quelle. Le candidat recevra la date, l'adresse,
            le contact sur place et une invitation d'agenda.
          </p>
          <div className="besoin-conv-grille">
            <label>Qui est convoqué *
              <select className="erp-field" value={form.candidate_id} style={bord('candidate_id')}
                onChange={e => maj('candidate_id', e.target.value)}>
                <option value="">Choisissez…</option>
                {convocables.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
              {/* Convoquer quelqu'un qui n'est pas sur le besoin n'a pas de
                  sens : on n'a pas proposé son profil au client. */}
              {!convocables.length && <small className="besoin-conv-erreur">Aucun candidat positionné sur ce besoin — positionnez-le d'abord dans l'onglet Candidats.</small>}
            </label>
            <label>Date et heure *
              <input type="datetime-local" className="erp-field" value={form.debut} style={bord('debut')}
                onChange={e => maj('debut', e.target.value)} />
              {sur('debut') && <small className="besoin-conv-erreur" role="alert">{champErreur.message}</small>}
            </label>
            <label>Durée (minutes)
              <input type="number" min="15" max="480" step="15" className="erp-field" value={form.duree_min}
                onChange={e => maj('duree_min', e.target.value)} />
            </label>
            <label className="besoin-conv-large">Adresse du rendez-vous *
              <textarea className="erp-field" rows={2} value={form.lieu} style={bord('lieu')}
                placeholder="Rue du Rhône 12, 1204 Genève — bâtiment B, 3e étage"
                onChange={e => maj('lieu', e.target.value)} />
              {sur('lieu') && <small className="besoin-conv-erreur" role="alert">{champErreur.message}</small>}
            </label>
            <label>Qui demander sur place
              <input className="erp-field" value={form.contact_nom} placeholder="Mme Dubois"
                onChange={e => maj('contact_nom', e.target.value)} />
            </label>
            <label>Son téléphone
              <input className="erp-field" value={form.contact_tel} placeholder="+41 22 000 00 00"
                onChange={e => maj('contact_tel', e.target.value)} />
            </label>
            {/* Le modèle REMPLACE l'email intégré. On ne cache aucun modèle :
                celui qui ne convient pas ici est annoté, pas escamoté —
                sinon on croit qu'il n'existe pas. */}
            {modeles.length > 0 && (
              <label className="besoin-conv-large">Modèle d'email
                <select className="erp-field" value={form.template_id}
                  onChange={e => maj('template_id', e.target.value)}>
                  <option value="">Email standard (date, adresse, contact sur place)</option>
                  {modeles.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.nom}{m.utilisable === false ? ' — incompatible ici' : ''}
                    </option>
                  ))}
                </select>
                {modeleChoisi?.utilisable === false ? (
                  <small className="besoin-conv-erreur" role="alert">
                    Ce modèle utilise {modeleChoisi.hors_contexte.map(v => `{${v}}`).join(', ')} — ces
                    variables n'existent pas dans une convocation. Corrigez le modèle dans
                    Réglages &gt; Notifications, ou gardez l'email standard.
                  </small>
                ) : (
                  <small className="bd-sect-hint">
                    {form.template_id
                      ? 'Ce modèle remplace l\'email standard. L\'invitation d\'agenda part quand même.'
                      : 'Les modèles sont ceux de votre bibliothèque d\'emails, partagée avec les notifications.'}
                  </small>
                )}
              </label>
            )}
            <label className="besoin-conv-large">Consigne particulière
              <textarea className="erp-field" rows={2} value={form.consigne}
                placeholder="Se présenter à la réception avec une pièce d'identité."
                onChange={e => maj('consigne', e.target.value)} />
            </label>
          </div>
          {champErreur && !['candidate_id', 'debut', 'lieu'].includes(champErreur.champ) && (
            <div className="besoin-conv-erreur" role="alert">{champErreur.message}</div>
          )}
          <div className="besoin-conv-pied">
            <span className="bd-sect-hint">La convocation part immédiatement — vérifiez la date et l'adresse.</span>
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => { setForm(null); setChampErreur(null); }}>Annuler</button>
            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={envoyer} disabled={busy}>
              <BI icon={busy ? 'arrow-repeat' : 'send'} style={busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
              {busy ? 'Envoi…' : 'Envoyer la convocation'}
            </button>
          </div>
        </div>
      )}

      {liste === null ? (
        <div className="bd-sect-hint"><BI icon="arrow-repeat" /> Chargement…</div>
      ) : Array.isArray(liste) && liste.length === 0 && !form ? (
        <div className="bd-sect-hint">Personne n'a encore été convoqué sur ce besoin.</div>
      ) : Array.isArray(liste) ? (
        <div className="besoin-conv-liste">
          {liste.map(e => {
            const [etat, ton] = ETAT[e.statut] || [e.statut, 'mut'];
            return (
              <div className="besoin-conv-ligne" key={e.id}>
                <div className="besoin-conv-qui">
                  <strong>{nomDe(e.candidat)}</strong>
                  <span className={`erp-chip besoin-conv-etat besoin-conv-etat--${ton}`}>{etat}</span>
                </div>
                <div className="besoin-conv-quand">{quand(e.creneau?.debut) || 'Date non fixée'}</div>
                {e.lieu && <div className="besoin-conv-lieu"><BI icon="geo-alt" /> {e.lieu}</div>}
                {e.contact?.nom && <div className="besoin-conv-contact">Sur place : {[e.contact.nom, e.contact.tel].filter(Boolean).join(' · ')}</div>}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
